import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/Layout';
import { delivery, drivers, orders as ordersApi } from '@/lib/api';
import { formatCurrency, getStatusColor, getStatusLabel } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, User, Clock, Package, Truck, Building2 } from 'lucide-react';
import { toast } from 'sonner';

// Tipos de entrega parceira (não podem ser alterados)
const PARTNER_DELIVERY_TYPES = ['IFOOD', '99FOOD', 'KEETA', 'RAPPI', 'UBER_EATS'];

// Labels para tipos de entrega
const DELIVERY_TYPE_LABELS = {
  'LOJA': 'Entrega Loja',
  'IFOOD': 'iFood',
  '99FOOD': '99Food',
  'KEETA': 'Keeta',
  'RAPPI': 'Rappi',
  'UBER_EATS': 'Uber Eats'
};

// Cores para tipos de entrega
const DELIVERY_TYPE_COLORS = {
  'LOJA': 'bg-blue-100 text-blue-800',
  'IFOOD': 'bg-red-100 text-red-800',
  '99FOOD': 'bg-yellow-100 text-yellow-800',
  'KEETA': 'bg-purple-100 text-purple-800',
  'RAPPI': 'bg-orange-100 text-orange-800',
  'UBER_EATS': 'bg-green-100 text-green-800'
};

export function DeliveryPage() {
  const [deliveries, setDeliveries] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [editingDeliveryType, setEditingDeliveryType] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deliveriesRes, driversRes] = await Promise.all([
        delivery.list(),
        drivers.list({ available_only: false }) // Carregar TODOS os motoristas
      ]);
      setDeliveries(deliveriesRes.data);
      setDriversList(driversRes.data || []);
      console.log('Drivers carregados:', driversRes.data); // Debug
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriver || !selectedDelivery) return;

    try {
      await delivery.assignDriver(selectedDelivery.id, selectedDriver);
      toast.success('Entregador atribuído com sucesso!');
      loadData();
      setSelectedDelivery(null);
      setSelectedDriver('');
    } catch (error) {
      toast.error('Erro ao atribuir entregador');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleUpdateDeliveryType = async (deliveryId, newType) => {
    try {
      await delivery.updateDeliveryType(deliveryId, newType);
      toast.success('Tipo de entrega atualizado!');
      loadData();
      setEditingDeliveryType(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar tipo de entrega');
    }
  };

  const isPartnerDelivery = (deliveryType) => {
    return PARTNER_DELIVERY_TYPES.includes(deliveryType);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  const statusCounts = {
    aguardando: deliveries.filter(d => d.dispatcher_status === 'AGUARDANDO').length,
    atribuido: deliveries.filter(d => d.dispatcher_status === 'ATRIBUIDO').length,
    em_rota: deliveries.filter(d => d.dispatcher_status === 'EM_ROTA').length,
  };

  // Separar entregas por tipo
  const partnerDeliveries = deliveries.filter(d => isPartnerDelivery(d.delivery_type));
  const storeDeliveries = deliveries.filter(d => !isPartnerDelivery(d.delivery_type));

  return (
    <MainLayout>
      <div className="space-y-8 page-enter" data-testid="delivery-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Gestão de Delivery
          </h1>
          <p className="text-slate-600">Gerencie entregas e entregadores</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="text-orange-500" size={24} />
              <span className="text-sm text-slate-500">Aguardando</span>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {statusCounts.aguardando}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="text-blue-500" size={24} />
              <span className="text-sm text-slate-500">Atribuídos</span>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {statusCounts.atribuido}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="text-emerald-500" size={24} />
              <span className="text-sm text-slate-500">Em Rota</span>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {statusCounts.em_rota}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Truck className="text-red-500" size={24} />
              <span className="text-sm text-slate-500">Parceiros</span>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {partnerDeliveries.length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              Entregas Ativas
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Pedido</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo Entrega</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Endereço</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Entregador</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Valor</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Tempo Parado</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.length > 0 ? (
                  deliveries.map((deliv) => {
                    const deliveryType = deliv.delivery_type || 'LOJA';
                    const isPartner = isPartnerDelivery(deliveryType);
                    
                    return (
                      <tr
                        key={deliv.id}
                        data-testid={`delivery-row-${deliv.id}`}
                        className="bg-white hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                      >
                        <td className="px-6 py-4 text-sm text-slate-600 font-medium">
                          {deliv.order?.order_number ? `#${deliv.order.order_number}` : `#${deliv.order_id.substring(0, 8).toUpperCase()}`}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                          {deliv.order?.customer_name || 'Anônimo'}
                        </td>
                        <td className="px-6 py-4">
                          {editingDeliveryType === deliv.id ? (
                            <Select 
                              defaultValue={deliveryType}
                              onValueChange={(value) => handleUpdateDeliveryType(deliv.id, value)}
                            >
                              <SelectTrigger className="w-32" data-testid={`delivery-type-select-${deliv.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="LOJA">Entrega Loja</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span 
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DELIVERY_TYPE_COLORS[deliveryType] || 'bg-gray-100 text-gray-800'}`}
                                data-testid={`delivery-type-badge-${deliv.id}`}
                              >
                                {isPartner ? (
                                  <span className="flex items-center gap-1">
                                    <Truck size={12} />
                                    {DELIVERY_TYPE_LABELS[deliveryType] || deliveryType}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Building2 size={12} />
                                    {DELIVERY_TYPE_LABELS[deliveryType] || deliveryType}
                                  </span>
                                )}
                              </span>
                              {!isPartner && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => setEditingDeliveryType(deliv.id)}
                                  data-testid={`edit-delivery-type-btn-${deliv.id}`}
                                >
                                  Alterar
                                </Button>
                              )}
                              {isPartner && (
                                <span className="text-xs text-slate-400" title="Entregas parceiras não podem ser alteradas">
                                  🔒
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {deliv.address?.street}, {deliv.address?.number} - {deliv.address?.neighborhood}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {isPartner ? (
                            <span className="text-slate-400 italic">Via parceiro</span>
                          ) : (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant={deliv.driver_name ? "ghost" : "outline"}
                                  onClick={() => setSelectedDelivery(deliv)}
                                  data-testid={`assign-driver-btn-${deliv.id}`}
                                  className={deliv.driver_name ? "flex items-center gap-2 text-slate-600 hover:text-slate-900" : ""}
                                >
                                  {deliv.driver_name ? (
                                    <>
                                      <User size={16} />
                                      {deliv.driver_name}
                                      <span className="text-xs text-blue-500 ml-1">(alterar)</span>
                                    </>
                                  ) : (
                                    "Atribuir"
                                  )}
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>{deliv.driver_name ? 'Alterar' : 'Atribuir'} Entregador</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                                    <SelectTrigger data-testid="driver-select">
                                      <SelectValue placeholder="Selecione um entregador" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {driversList.filter(d => d.is_active !== false).map((driver) => (
                                        <SelectItem key={driver.id} value={driver.id}>
                                          {driver.name} {driver.is_available ? '✓' : ''}
                                        </SelectItem>
                                      ))}
                                      {driversList.filter(d => d.is_active !== false).length === 0 && (
                                        <div className="px-2 py-3 text-sm text-slate-500 text-center">
                                          Nenhum entregador cadastrado
                                        </div>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    onClick={handleAssignDriver}
                                    className="w-full bg-[#FF6B6B] hover:bg-[#FF7A5A]"
                                    data-testid="confirm-assign-btn"
                                    disabled={!selectedDriver}
                                  >
                                    Confirmar
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(deliv.order?.status)}`}>
                            {getStatusLabel(deliv.order?.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                          {formatCurrency(deliv.order?.total || 0)}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const ageMin = Math.floor((Date.now() - new Date(deliv.created_at).getTime()) / 60000);
                            const ageH = Math.floor(ageMin / 60);
                            const ageD = Math.floor(ageH / 24);
                            const label = ageD >= 1 ? (ageD + 'd ' + (ageH % 24) + 'h') : ageH >= 1 ? (ageH + 'h ' + (ageMin % 60) + 'm') : (ageMin + 'm');
                            const color = ageMin > 120 ? 'bg-red-100 text-red-700 border-red-300' : ageMin > 30 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-100 text-slate-600 border-slate-200';
                            const icon = ageMin > 120 ? String.fromCodePoint(0x26A0) : ageMin > 30 ? String.fromCodePoint(0x1F7E1) : String.fromCodePoint(0x1F7E2);
                            return (
                              <span
                                className={'px-2.5 py-1 rounded-full text-xs font-semibold border ' + color}
                                title={'Ha ' + label + ' parado em ' + (deliv.order?.status || '')}
                                data-testid={'delivery-age-' + deliv.id}
                              >
                                {icon} {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          {deliv.order?.status === 'CONFIRMADO' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(deliv.order_id, 'PREPARO')}
                              data-testid={`start-prep-btn-${deliv.id}`}
                            >
                              Iniciar Preparo
                            </Button>
                          )}
                          {deliv.order?.status === 'PREPARO' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(deliv.order_id, 'SAIU')}
                              data-testid={`mark-out-btn-${deliv.id}`}
                            >
                              Saiu p/ Entrega
                            </Button>
                          )}
                          {deliv.order?.status === 'SAIU' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(deliv.order_id, 'ENTREGUE')}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`mark-delivered-btn-${deliv.id}`}
                            >
                              Entregue
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                      Nenhuma entrega ativa
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Seção de entregadores disponíveis */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              Entregadores Disponíveis
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {driversList.filter(d => d.is_available).length > 0 ? (
                driversList.filter(d => d.is_available).map((driver) => (
                  <div 
                    key={driver.id} 
                    className="border border-slate-200 rounded-lg p-4 flex items-center gap-3"
                    data-testid={`available-driver-${driver.id}`}
                  >
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="text-emerald-600" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{driver.name}</p>
                      <p className="text-sm text-slate-500">{driver.phone}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 col-span-full">Nenhum entregador disponível</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
